import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { invoicesService } from '../services/api';
import { fmtCurrency, fmtDate, fmtDateTime, getTodayStr } from '../utils/formatters';
import { TONE_BADGE } from '../utils/statusColors';
import {
  Acao, agruparFila, FAIXAS_DE_ATRASO, FaixaDeAtraso, faixaDaUrl, GRUPO, GrupoDaFila, LinhaDaCobranca, Motivo,
  PARAMETRO_DA_FAIXA, Papel, papelNaCobranca, proximoPasso, ResumoDaFila, resumoDaFilaValido, seloDaCobranca,
} from '../utils/filaDeCobranca';
import { trocarParametroNaUrl } from '../config/rotas';
import { Icon } from './shared/Icon';
import { campo, EstadoDaLista, falhaIgnoradaDeProposito, mensagemDoErroDeCarga, opcaoAtualForaDaLista, useEstadoDaCarga } from '../design';
import { barraDoEstado, CartaoDoTopo, CascaDaLista } from './shared/lista/PecasDaLista';
import CascaDeGaveta from './shared/CascaDeGaveta';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import ContatosDaFatura from './cobranca/ContatosDaFatura';
import SugestoesDeCobranca from './cobranca/SugestoesDeCobranca';
import ModalDeCobranca, { CandidatoDeCobranca } from './cobranca/ModalDeCobranca';

/**
 * Financeiro › Cobrança (decisão E1 do plano de cobrança, 30/10/2026): as faturas vencidas separadas pelo MOTIVO, cada
 * linha com o próximo passo e o botão dele, e a gaveta Contatos com o que o cliente disse e prometeu.
 * Prévia: https://claude.ai/artifact/E1gGoqTdCgJerRZUZb4nUA — as regras da fila estão em utils/filaDeCobranca.ts.
 *
 * Os botões seguem os papéis do BACKEND, para a tela não oferecer o que devolve 403: Cobrar e Pausar/Retomar são
 * ADMIN; registrar contato e reenviar são ADMIN e EDITOR; vendedor e leitor só leem.
 *
 * Desde a unificação (30/10/2026, E1; prévia https://claude.ai/artifact/ScP3EnBjNsf4BFYeHtD9gm) a cobrança MANUAL mora
 * aqui e não em Faturas: "Cobrar" na linha e a seleção com "Cobrar N" abrem o ModalDeCobranca, o mesmo envio de antes.
 * O T1 acabou: "Cobrar" não leva mais a Faturas. "Abrir fatura" continua onde o passo é da fatura (a nota).
 */

/** Quantas linhas um grupo mostra antes do "Ver as N". */
const LINHAS_VISIVEIS = 3;

const ROTULO_DA_ACAO: Record<Acao, string> = {
  reenviar: 'Reenviar',
  abrirFatura: 'Abrir fatura',
  cobrar: 'Cobrar',
  contato: 'Registrar contato',
  pausar: 'Pausar',
  retomar: 'Retomar',
};

const ICONE: Record<string, string> = { danger: 'text-red-400', warning: 'text-amber-400' };
const fantasma = 'px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap border border-central-borda bg-central-cartao text-central-texto hover:bg-central-elevado disabled:opacity-40';

type Aba = 'fila' | 'sugestoes';

/**
 * S1 (fase 2, 30/10/2026): duas sub-abas, porque são duas perguntas, "o que fazer com cada vencida" e "o que aprovar
 * hoje". As duas ficam montadas, para o rótulo "Sugestões · N" saber quantas esperam sem a sub-aba estar aberta.
 */
export default function CobrancaTab({ onAbrirFatura, onAbrirOficios, foco, onFocoConsumido, onResumo }: {
  /** "Abrir fatura" leva a Faturas com a fatura buscada (o passo que é da fatura, como emitir a nota). */
  onAbrirFatura: (code: string) => void;
  /** Depois de criar o rascunho de um ofício, "Abrir em Ofícios". */
  onAbrirOficios?: () => void;
  /** B1: o selo de Faturas manda o código, e a fila abre na linha dele. */
  foco?: string | null;
  onFocoConsumido?: () => void;
  /** H1: o resumo da fila, lido junto com a lista, sobe para a faixa do topo (App). `null` = não leu. */
  onResumo?: (r: ResumoDaFila | null) => void;
}) {
  const [aba, setAba] = useState<Aba>('fila');
  useEffect(() => { if (foco) setAba('fila'); }, [foco]);
  const [esperando, setEsperando] = useState<number | null>(null);
  const rotulo = (a: Aba) => (a === 'fila' ? 'Fila por motivo' : `Sugestões${esperando === null ? '' : ` · ${esperando}`}`);
  return (
    <div className='space-y-4'>
      <div role='tablist' aria-label='Cobrança' className='flex gap-1 border-b border-central-borda'>
        {(['fila', 'sugestoes'] as Aba[]).map((a) => (
          <button
            key={a}
            type='button'
            role='tab'
            aria-selected={aba === a}
            data-sub-aba={a}
            onClick={() => setAba(a)}
            className={`px-3 py-2 -mb-px text-sm border-b-2 ${aba === a ? 'border-blue-500 text-central-texto font-semibold' : 'border-transparent text-central-texto-fraco hover:text-central-texto'}`}
          >
            {rotulo(a)}
          </button>
        ))}
      </div>
      <div hidden={aba !== 'fila'}><FilaPorMotivo onAbrirFatura={onAbrirFatura} foco={foco} onFocoConsumido={onFocoConsumido} onResumo={onResumo} /></div>
      <div hidden={aba !== 'sugestoes'}><SugestoesDeCobranca onContagem={setEsperando} onAbrirOficios={onAbrirOficios} /></div>
    </div>
  );
}

function FilaPorMotivo({ onAbrirFatura, foco, onFocoConsumido, onResumo }: {
  onAbrirFatura: (code: string) => void;
  foco?: string | null;
  onFocoConsumido?: () => void;
  onResumo?: (r: ResumoDaFila | null) => void;
}) {
  const papel = useMemo(papelNaCobranca, []);
  const avisar = useAviso();
  const confirmar = useConfirm();
  const carga = useEstadoDaCarga();
  const [linhas, setLinhas] = useState<LinhaDaCobranca[]>([]);
  const [abertos, setAbertos] = useState<Set<Motivo>>(new Set());
  const [contatosDe, setContatosDe] = useState<LinhaDaCobranca | null>(null);
  const [pausarDe, setPausarDe] = useState<LinhaDaCobranca | null>(null);
  const [reenviarDe, setReenviarDe] = useState<LinhaDaCobranca | null>(null);
  const [selecao, setSelecao] = useState<Set<number>>(new Set());
  const [cobrarIds, setCobrarIds] = useState<number[] | null>(null);
  const [destaque, setDestaque] = useState<string | null>(null);
  /** C1: o cartão de motivo filtra a lista para aquele grupo. `null` = todos. */
  const [filtro, setFiltro] = useState<Motivo | null>(null);
  /**
   * A2/K1 (02/11/2026): a faixa de atraso recorta TODOS os grupos ao mesmo tempo, e cada grupo diz "N de M". Fica na URL
   * (`?atraso=`), que é como o cartão do Dashboard abre a Cobrança já filtrada. `null` = todas.
   */
  const [faixa, setFaixa] = useState<FaixaDeAtraso | null>(() => faixaDaUrl(window.location.search));
  const [resumo, setResumo] = useState<ResumoDaFila | null>(null);

  const carregar = useCallback(() => carga.rodar(async () => {
    // O resumo é da MESMA consulta da lista e é lido no mesmo gesto, para o topo e a barra de faixas não serem de outro
    // momento. Ele falhar não derruba a lista: as faixas ficam em "—" e o filtro continua, porque a faixa vem na linha.
    const [r, resumoLido] = await Promise.all([
      invoicesService.dunningQueue(),
      Promise.resolve().then(() => invoicesService.dunningQueueSummary()).catch(falhaIgnoradaDeProposito('resumo da fila: sem ele as pílulas de faixa e a faixa do topo dizem "—", e o filtro segue, porque a faixa vem em cada linha')),
    ]);
    setLinhas(Array.isArray(r) ? r : []);
    const valido = resumoDaFilaValido(resumoLido);
    setResumo(valido);
    onResumo?.(valido);
  }), [carga.rodar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregar(); }, [carregar]);

  const naFaixa = useMemo(() => (faixa ? linhas.filter((l) => l.faixaDeAtraso === faixa) : linhas), [linhas, faixa]);
  const grupos = useMemo(() => agruparFila(naFaixa), [naFaixa]);
  /** O M do "N de M": quantas cada grupo tem sem a faixa. */
  const semFaixa = useMemo(() => new Map(agruparFila(linhas).map((g) => [g.motivo, g.linhas.length])), [linhas]);
  const escolherFaixa = (f: FaixaDeAtraso | null) => {
    setFaixa(f);
    trocarParametroNaUrl(PARAMETRO_DA_FAIXA, f);
    // A barra de baixo não pode cobrar o que a faixa escondeu.
    if (f) setSelecao((s) => new Set(Array.from(s).filter((id) => porId.get(id)?.faixaDeAtraso === f)));
  };

  // B1: chegou de Faturas pelo selo. Abre o grupo onde a fatura está, rola até a linha e marca por uns segundos.
  useEffect(() => {
    if (!foco || !carga.atualizadoEm) return;
    const g = agruparFila(linhas).find((x) => x.linhas.some(({ linha }) => linha.code === foco));
    if (g) setAbertos((s) => new Set(s).add(g.motivo));
    if (g) setFiltro(null);
    // A fatura pedida fora da faixa escolhida: a faixa sai, senão a linha não aparece.
    if (g && faixa && !naFaixa.some((l) => l.code === foco)) escolherFaixa(null);
    setDestaque(g ? foco : null);
    if (!g) avisar(`${foco} não está entre as vencidas da cobrança agora.`, 'info');
    onFocoConsumido?.();
  }, [foco, carga.atualizadoEm]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!destaque) return;
    document.querySelector(`[data-linha="${destaque}"]`)?.scrollIntoView?.({ block: 'center' });
    const t = setTimeout(() => setDestaque(null), 4000);
    return () => clearTimeout(t);
  }, [destaque]);

  const porId = useMemo(() => new Map(linhas.map((l) => [l.id, l])), [linhas]);
  const selecionadas = useMemo(() => Array.from(selecao).map((id) => porId.get(id)).filter(Boolean) as LinhaDaCobranca[], [selecao, porId]);
  const alternar = (id: number) => setSelecao((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Com faixa, o grupo sem nenhuma nela some: o "Nenhum órgão público vencido" mentiria sobre a carteira.
  const visiveis = grupos.filter((g) => (filtro ? g.motivo === filtro : (g.motivo !== 'outras' && !faixa) || g.linhas.length > 0));
  const aCobrar = (cobrarIds || []).map((id) => porId.get(id)).filter(Boolean) as CandidatoDeCobranca[];
  const total = useMemo(() => linhas.reduce((s, l) => s + (Number(l.originalValue) || 0), 0), [linhas]);

  const retomar = async (l: LinhaDaCobranca) => {
    const ok = await confirmar({
      title: `Retomar a cobrança de ${l.code}?`,
      message: `A pausa${l.snoozeReason ? ` ("${l.snoozeReason}")` : ''} acaba agora e a fatura volta para a fila.`,
      confirmLabel: 'Retomar',
    });
    if (!ok) return;
    try {
      await invoicesService.snoozeDunning(l.id, null, null);
      avisar(`Cobrança de ${l.code} retomada.`, 'success');
      carregar();
    } catch (e) {
      avisar(`Não retomou: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  const agir = (a: Acao, l: LinhaDaCobranca) => {
    if (a === 'abrirFatura') onAbrirFatura(l.code);
    else if (a === 'cobrar') setCobrarIds([l.id]);
    else if (a === 'contato') setContatosDe(l);
    else if (a === 'pausar') setPausarDe(l);
    else if (a === 'retomar') retomar(l);
    else if (a === 'reenviar') setReenviarDe(l);
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-end justify-between gap-3 flex-wrap'>
        <div>
          <p className='text-sm text-central-texto-fraco'>Vencidas</p>
          <p className='text-2xl font-bold text-central-texto tabular-nums' data-total-vencidas>
            {carga.atualizadoEm ? `${linhas.length} ${linhas.length === 1 ? 'fatura' : 'faturas'} · ${fmtCurrency(total)}` : '—'}
          </p>
          {carga.atualizadoEm && <p className='text-xs text-central-texto-fraco mt-0.5'>Lido em {fmtDateTime(carga.atualizadoEm)}</p>}
        </div>
        <button type='button' className={fantasma} onClick={carregar}>Atualizar</button>
      </div>

      <EstadoDaLista
        carga={carga}
        temDados={carga.atualizadoEm !== null}
        oQue='as faturas vencidas'
        onTentarDeNovo={carregar}
        vazio={<p className='text-central-texto-fraco py-10 text-center'>Nenhuma fatura vencida.</p>}
      >
        {linhas.length === 0 ? (
          <p className='text-central-texto-fraco py-10 text-center' data-sem-vencidas>Nenhuma fatura vencida.</p>
        ) : (
          <div className='space-y-3'>
            <div role='group' aria-label='Dias de atraso' className='flex flex-wrap gap-1.5' data-faixas-de-atraso>
              {/* Os números das pílulas vêm do resumo (régua do servidor). Sem resumo, "—": a pílula continua filtrando. */}
              {[
                { chave: null as FaixaDeAtraso | null, rotulo: 'Todas', n: resumo?.aReceber ?? null },
                ...FAIXAS_DE_ATRASO.map((x) => ({ chave: x.chave as FaixaDeAtraso | null, rotulo: x.rotulo, n: resumo?.aging[x.chave] ?? null })),
              ].map((f) => {
                const ativa = faixa === f.chave;
                const count = f.n?.count ?? null;
                return (
                  <button
                    key={f.chave ?? 'todas'}
                    type='button'
                    aria-pressed={ativa}
                    data-faixa={f.chave ?? 'todas'}
                    disabled={!!f.chave && count === 0 && !ativa}
                    onClick={() => escolherFaixa(f.chave)}
                    className={`px-3 py-1.5 rounded-lg border text-sm text-left disabled:opacity-40 ${ativa ? 'border-blue-500/70 bg-central-elevado text-central-texto font-semibold' : 'border-central-borda bg-central-cartao text-central-texto-fraco hover:bg-central-elevado'}`}
                  >
                    <span className='text-central-texto'>{f.rotulo}</span>
                    <span className='ml-2 tabular-nums'>{count === null ? '—' : `${count}${count ? ` · ${fmtCurrency(f.n!.value)}` : ''}`}</span>
                  </button>
                );
              })}
            </div>
            {faixa && naFaixa.length === 0 && (
              <p className='text-central-texto-fraco py-6 text-center' data-faixa-vazia>Nenhuma vencida nesta faixa.</p>
            )}
            {/* C1: um cartão por motivo, com contagem e valor. Clicar filtra; clicar de novo, ou "ver todos", volta. Cartão
                com zero fica apagado e não filtra: não há o que mostrar. */}
            <div className='grid gap-2 grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]' data-cartoes-de-motivo>
              {grupos.filter((g) => g.motivo !== 'outras' || g.linhas.length > 0).map((g) => {
                const info = GRUPO[g.motivo];
                const n = g.linhas.length;
                return (
                  <CartaoDoTopo
                    key={g.motivo}
                    data-cartao={g.motivo}
                    rotulo={<span className='inline-flex items-center gap-1.5'><Icon name={info.icone} className='w-4 h-4' />{info.curto}</span>}
                    valor={n}
                    sub={n ? fmtCurrency(g.total) : '—'}
                    ativo={filtro === g.motivo}
                    barra={n ? info.tom : null}
                    onClick={n ? () => setFiltro((f) => (f === g.motivo ? null : g.motivo)) : undefined}
                    className={n ? '' : 'opacity-50'}
                  />
                );
              })}
            </div>
            {filtro && (
              <p className='text-sm text-central-texto-fraco' data-filtrando>
                Filtrando: <b className='text-central-texto'>{GRUPO[filtro].curto}</b> ·{' '}
                <button type='button' className='underline hover:text-central-texto' onClick={() => setFiltro(null)}>ver todos</button>
              </p>
            )}
            {visiveis.map((g) => (
              <Grupo
                key={g.motivo}
                grupo={g}
                deTotal={faixa ? semFaixa.get(g.motivo) ?? 0 : null}
                papel={papel}
                aberto={!!filtro || abertos.has(g.motivo)}
                onAbrir={() => setAbertos((s) => new Set(s).add(g.motivo))}
                onAcao={agir}
                onContatos={setContatosDe}
                selecao={papel.admin ? selecao : null}
                onSelecionar={alternar}
                destaque={destaque}
              />
            ))}
          </div>
        )}
      </EstadoDaLista>

      {/* B1: presa no RODAPÉ da área e no fluxo da página, depois da lista: o fim da lista fica sempre acima dela, e a
          linha que se seleciona rola para fora de baixo dela (scroll-mb nas linhas). Flutuando sobre a lista, como era,
          escondia a linha de baixo. */}
      {papel.admin && selecionadas.length > 0 && (
        <div className='sticky bottom-0 z-10 -mx-1 rounded-t-xl border border-blue-500/60 bg-central-cartao px-4 py-3 flex items-center gap-3 flex-wrap shadow-[0_-8px_16px_rgba(0,0,0,0.35)]' data-barra-de-cobranca>
          <span className='text-sm text-central-texto'>
            <b>{selecionadas.length} {selecionadas.length === 1 ? 'selecionada' : 'selecionadas'}</b> · {fmtCurrency(selecionadas.reduce((t, l) => t + (Number(l.originalValue) || 0), 0))}
          </span>
          <span className='flex-1' />
          <button type='button' className={fantasma} onClick={() => setSelecao(new Set())}>Limpar</button>
          <button type='button' className={fantasma} onClick={() => setCobrarIds(selecionadas.map((l) => l.id))}>Cobrar {selecionadas.length}</button>
        </div>
      )}

      {cobrarIds && aCobrar.length > 0 && (
        <ModalDeCobranca
          candidatos={aCobrar}
          onClose={() => { setCobrarIds(null); setSelecao(new Set()); }}
          onMudou={carregar}
        />
      )}

      {contatosDe && (
        <CascaDeGaveta
          titulo={contatosDe.clientName}
          subtitulo={`Contatos · ${contatosDe.code} · vencida em ${fmtDate(contatosDe.dueDate)}`}
          onClose={() => setContatosDe(null)}
          largura='painel'
          rotulo={`Contatos da fatura ${contatosDe.code}`}
        >
          <ContatosDaFatura invoiceId={contatosDe.id} clientId={contatosDe.clientId} podeRegistrar={papel.registra} aoMudar={carregar} />
        </CascaDeGaveta>
      )}
      {pausarDe && <ModalPausar linha={pausarDe} onClose={() => setPausarDe(null)} onPausou={() => { setPausarDe(null); carregar(); }} />}
      {reenviarDe && <ModalReenviar linha={reenviarDe} onClose={() => setReenviarDe(null)} onReenviou={() => { setReenviarDe(null); carregar(); }} />}
    </div>
  );
}

function Grupo({ grupo, deTotal, papel, aberto, onAbrir, onAcao, onContatos, selecao, onSelecionar, destaque }: {
  grupo: GrupoDaFila;
  /** Com faixa de atraso escolhida, quantas o grupo tem sem ela (o M do "N de M"). `null` = sem faixa. */
  deTotal: number | null;
  papel: Papel;
  aberto: boolean;
  onAbrir: () => void;
  onAcao: (a: Acao, l: LinhaDaCobranca) => void;
  onContatos: (l: LinhaDaCobranca) => void;
  /** Só admin seleciona (as rotas de envio são ADMIN); `null` esconde a caixa. */
  selecao: Set<number> | null;
  onSelecionar: (id: number) => void;
  destaque: string | null;
}) {
  const info = GRUPO[grupo.motivo];
  const visiveis = aberto ? grupo.linhas : grupo.linhas.slice(0, LINHAS_VISIVEIS);
  const escondidas = grupo.linhas.length - visiveis.length;
  return (
    <CascaDaLista>
      <section aria-label={info.titulo} data-grupo={grupo.motivo}>
        {/* Padrão da Central: o cabeçalho tem peso (fundo elevado, ícone, título maior) e a barra do tom, para o grupo não
            se confundir com as linhas. */}
        <header className={`px-4 py-3 border-b border-central-borda bg-central-elevado flex flex-wrap items-center gap-x-2 gap-y-0.5 ${barraDoEstado(info.tom)}`}>
          <Icon name={info.icone} className={`w-4 h-4 ${info.tom ? ICONE[info.tom] : 'text-central-texto-fraco'}`} />
          <h3 className='text-base font-semibold text-central-texto'>{info.titulo}</h3>
          <span className='text-sm text-central-texto-fraco tabular-nums' data-contagem>
            {grupo.linhas.length}{deTotal !== null ? ` de ${deTotal}` : ''}{grupo.linhas.length ? ` · ${fmtCurrency(grupo.total)}` : ''}
          </span>
          <p className='basis-full text-xs text-central-texto-fraco pl-6'>{info.sub}</p>
        </header>
        {grupo.linhas.length === 0 ? (
          <p className='m-3 px-3 py-2 rounded-lg border border-dashed border-central-borda text-sm text-central-texto-fraco'>{info.vazio}</p>
        ) : (
          <ul>
            {visiveis.map(({ linha: l, selos }) => {
              const { passo, detalhe, acoes } = proximoPasso(grupo.motivo, l, papel);
              const selo = seloDaCobranca(l);
              const marcada = !!selecao?.has(l.id);
              // Clicar em qualquer ponto da linha seleciona (a caixa é pequena demais para ser o único alvo). Botão, caixa,
              // link e campo seguem com o próprio clique. Só para quem pode cobrar: para os outros a linha não reage.
              const clicar = selecao ? (e: React.MouseEvent<HTMLLIElement>) => {
                if ((e.target as HTMLElement).closest('button, a, input, label, select, textarea')) return;
                onSelecionar(l.id);
                // A barra aparece no rodapé: a linha que se acabou de marcar sobe para fora de baixo dela.
                e.currentTarget.scrollIntoView?.({ block: 'nearest' });
              } : undefined;
              return (
                <li
                  key={l.id}
                  data-linha={l.code}
                  data-destaque={destaque === l.code || undefined}
                  data-marcada={marcada || undefined}
                  onClick={clicar}
                  className={`grid gap-x-4 gap-y-1 items-center px-4 py-3 border-b last:border-b-0 border-central-borda scroll-mb-24 even:bg-white/[0.02] ${selecao ? 'cursor-pointer hover:bg-white/[0.04] md:grid-cols-[1.25rem_minmax(0,1.3fr)_5rem_7rem_minmax(0,2fr)_auto]' : 'md:grid-cols-[minmax(0,1.3fr)_5rem_7rem_minmax(0,2fr)_auto]'} ${marcada ? '!bg-blue-500/10' : ''} ${barraDoEstado(info.tom)} ${destaque === l.code ? 'ring-2 ring-blue-500/60' : ''}`}
                >
                  {selecao && (
                    <div><input type='checkbox' aria-label={`Selecionar ${l.code}`} checked={selecao.has(l.id)} onChange={(e) => { onSelecionar(l.id); e.currentTarget.closest('li')?.scrollIntoView?.({ block: 'nearest' }); }} /></div>
                  )}
                  <div className='min-w-0'>
                    <div className='font-medium text-central-texto font-mono text-sm'>{l.code}</div>
                    <div className='text-xs text-central-texto-fraco truncate' title={l.clientName}>{l.clientName}</div>
                    {/* J1: a marca de órgão público em toda linha dele, em qualquer grupo (é ela que diz "sem multa e juros"). */}
                    {l.isPublicSector && (
                      <span className={`mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.neutral}`} data-orgao-publico title='Órgão público: cobrado sem multa e juros.'>
                        <Icon name='predio' className='w-3 h-3' />órgão público
                      </span>
                    )}
                  </div>
                  <div className='text-sm text-central-texto-fraco tabular-nums'>{l.daysOverdue} {l.daysOverdue === 1 ? 'dia' : 'dias'}</div>
                  <div className='text-sm text-central-texto tabular-nums md:text-right'>{fmtCurrency(l.originalValue)}</div>
                  <div className='text-sm min-w-0'>
                    {selo && <span className={`mr-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${TONE_BADGE.warning}`} data-selo-cobrada>{selo}</span>}
                    <span className='text-central-texto' data-passo>{passo}</span>{' '}
                    <span className='text-central-texto-fraco'>{detalhe}</span>
                    {selos.filter((s) => s !== 'orgao').length > 0 && (
                      <span className='ml-1 inline-flex flex-wrap gap-1 align-middle'>
                        {/* O selo "órgão" virou a marca de órgão público, ao lado do cliente. */}
                        {selos.filter((s) => s !== 'orgao').map((s) => <span key={s} className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.neutral}`} data-selo={s}>{GRUPO[s].selo}</span>)}
                      </span>
                    )}
                  </div>
                  <div className='flex flex-wrap gap-1.5 md:justify-end'>
                    {acoes.map((a) => (
                      <button key={a} type='button' className={fantasma} onClick={() => onAcao(a, l)}>{ROTULO_DA_ACAO[a]}</button>
                    ))}
                    {!acoes.includes('contato') && (
                      <button type='button' className={fantasma} onClick={() => onContatos(l)}>Contatos</button>
                    )}
                  </div>
                </li>
              );
            })}
            {escondidas > 0 && (
              <li className='px-4 py-2'>
                <button type='button' className='text-sm text-central-texto-fraco hover:text-central-texto underline' onClick={onAbrir}>
                  Ver {escondidas === 1 ? 'a outra' : `as outras ${escondidas}`}
                </button>
              </li>
            )}
          </ul>
        )}
      </section>
    </CascaDaLista>
  );
}

/** F1: pausa com motivo e data de revisão. Na data, a fatura volta sozinha (o backend compara a data; não há cron). */
function ModalPausar({ linha, onClose, onPausou }: { linha: LinhaDaCobranca; onClose: () => void; onPausou: () => void }) {
  const avisar = useAviso();
  const [ate, setAte] = useState('');
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const hoje = getTodayStr();
  const invalido = !ate || ate < hoje || !motivo.trim();
  const salvar = async () => {
    if (invalido || salvando) return;
    setSalvando(true);
    try {
      await invoicesService.snoozeDunning(linha.id, ate, motivo.trim());
      avisar(`Cobrança de ${linha.code} pausada até ${fmtDate(ate)}.`, 'success');
      onPausou();
    } catch (e) {
      avisar(`Não pausou: ${mensagemDoErroDeCarga(e)}`, 'error');
      setSalvando(false);
    }
  };
  return (
    <CascaDeModal
      titulo={`Pausar a cobrança de ${linha.code}`}
      subtitulo={linha.clientName}
      onClose={onClose}
      largura='xs'
      temEdicaoNaoSalva={() => !!ate || !!motivo.trim()}
      rodape={
        <div className='flex justify-end gap-2'>
          <button type='button' className={fantasma} onClick={onClose}>Cancelar</button>
          <button type='button' className={fantasma} disabled={invalido || salvando} onClick={salvar}>{salvando ? 'Pausando…' : 'Pausar'}</button>
        </div>
      }
    >
      <div className='space-y-3'>
        <label className='block text-xs text-central-texto-fraco'>Revisar em
          <input type='date' min={hoje} value={ate} onChange={(e) => setAte(e.target.value)} className={`mt-1 block px-3 py-1.5 rounded-lg text-sm ${campo}`} />
        </label>
        <label className='block text-xs text-central-texto-fraco'>Motivo
          <input type='text' value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder='Ex.: em negociação com a diretoria' className={`mt-1 block w-full px-3 py-1.5 rounded-lg text-sm ${campo}`} />
        </label>
        <p className='text-xs text-central-texto-fraco'>Até a data, ninguém cobra e a fatura sai do alerta diário. Na data, ela volta sozinha para a fila.</p>
      </div>
    </CascaDeModal>
  );
}

/** D3: o e-mail voltou. Reenvia para um dos OUTROS e-mails financeiros do cliente, que o backend calcula (sem o que falhou). */
function ModalReenviar({ linha, onClose, onReenviou }: { linha: LinhaDaCobranca; onClose: () => void; onReenviou: () => void }) {
  const avisar = useAviso();
  const [dados, setDados] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [para, setPara] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    invoicesService.deliveryIssues(linha.id)
      .then((r: any) => { setDados(r); setPara((r?.destinosAlternativos || [])[0] || ''); })
      .catch((e: any) => setErro(mensagemDoErroDeCarga(e)));
  }, [linha.id]);

  const abertas = (dados?.ocorrencias || []).filter((o: any) => o.aberta !== false);
  const pendencia = abertas[0];
  const destinos: string[] = dados?.destinosAlternativos || [];

  const enviar = async () => {
    if (!pendencia || !para || enviando) return;
    setEnviando(true);
    try {
      await invoicesService.resendDunning(linha.id, para, pendencia.emailLogId);
      avisar(`Cobrança de ${linha.code} reenviada para ${para}.`, 'success');
      onReenviou();
    } catch (e) {
      avisar(`Não reenviou: ${mensagemDoErroDeCarga(e)}`, 'error');
      setEnviando(false);
    }
  };

  return (
    <CascaDeModal
      titulo={`Reenviar a cobrança de ${linha.code}`}
      subtitulo={linha.clientName}
      onClose={onClose}
      largura='sm'
      temEdicaoNaoSalva={false}
      rodape={
        <div className='flex justify-end gap-2'>
          <button type='button' className={fantasma} onClick={onClose}>Cancelar</button>
          <button type='button' className={fantasma} disabled={!pendencia || !para || enviando} onClick={enviar}>{enviando ? 'Reenviando…' : 'Reenviar'}</button>
        </div>
      }
    >
      {erro ? (
        <p role='alert' className={`px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>Não consegui ler a pendência: {erro}</p>
      ) : !dados ? (
        <p className='text-sm text-central-texto-fraco'>Lendo a pendência…</p>
      ) : !pendencia ? (
        <p className='text-sm text-central-texto-fraco'>Esta fatura não tem mais e-mail devolvido em aberto.</p>
      ) : (
        <div className='space-y-3 text-sm'>
          {abertas.map((o: any) => (
            <div key={o.emailLogId} className='border-l-[3px] border-l-red-500 pl-3'>
              <div className='text-central-texto'>{o.recipientEmail} · {o.status === 'dropped' ? 'descartado' : 'devolvido'}{o.bouncedAt ? ` em ${fmtDateTime(o.bouncedAt)}` : ''}</div>
              {o.bounceReason && <div className='text-xs text-central-texto-fraco'>{o.bounceReason}</div>}
            </div>
          ))}
          {destinos.length === 0 ? (
            <p className='text-amber-300'>O cliente não tem outro e-mail financeiro cadastrado. Cadastre um no cliente para poder reenviar.</p>
          ) : (
            <label className='block text-xs text-central-texto-fraco'>Reenviar para
              <select value={para} onChange={(e) => setPara(e.target.value)} className={`mt-1 block w-full px-3 py-1.5 rounded-lg text-sm ${campo}`}>
                {opcaoAtualForaDaLista(destinos.map((d) => ({ id: d })), para, para, false)}
                {destinos.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </CascaDeModal>
  );
}
